import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useDocumentRequests, useVoidDocumentRequest, type DocumentAnchor } from '../../hooks/useDocuments'
import { usePermissions } from '../../hooks/useAuth'
import {
  DOCUMENT_CATEGORY_LABELS,
  type DocumentCategory,
  type DocumentRequestStatus,
  type DocumentRequestSummary,
} from '../../lib/api'

interface Props {
  anchor: DocumentAnchor
}

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now()
  if (diff <= 0) return 'Expired'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h left`
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  if (hours > 0) return `${hours}h ${mins}m left`
  return `${mins}m left`
}

function formatAbsoluteDateTime(value: string): string {
  const d = new Date(value)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const STATUS_STYLES: Record<DocumentRequestStatus, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending' },
  fulfilled: { bg: 'bg-green-50', text: 'text-green-700', label: 'Fulfilled' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Expired' },
  voided: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Voided' },
}

function CategoryPills({ request }: { request: DocumentRequestSummary }) {
  return (
    <div className="flex flex-wrap gap-1">
      {request.categories.map((cat: DocumentCategory) => {
        const isUploaded = request.uploaded_categories.includes(cat)
        return (
          <span
            key={cat}
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs ${
              isUploaded
                ? 'bg-green-100 text-green-700 line-through'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            {isUploaded && (
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {DOCUMENT_CATEGORY_LABELS[cat] ?? cat}
          </span>
        )
      })}
    </div>
  )
}

interface RequestRowProps {
  request: DocumentRequestSummary
  anchor: DocumentAnchor
  variant: 'active' | 'history'
}

function RequestRow({ request, anchor, variant }: RequestRowProps) {
  const style = STATUS_STYLES[request.status] ?? STATUS_STYLES.pending
  const voidMutation = useVoidDocumentRequest()
  const canVoid = variant === 'active' && request.status === 'pending'
  const isVoiding = voidMutation.isPending && voidMutation.variables?.documentRequestId === request.id

  const handleVoid = () => {
    if (!window.confirm('Void this document request? The merchant upload link will stop working immediately.')) return
    voidMutation.mutate({ documentRequestId: request.id, anchor })
  }

  return (
    <div
      className={`p-3 rounded-md border ${
        variant === 'active' ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
        >
          {style.label}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {request.status === 'pending' && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatTimeRemaining(request.expires_at)}
            </span>
          )}
          {canVoid && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleVoid}
              isLoading={isVoiding}
              className="text-red-600 hover:bg-red-50 px-2 py-0.5 text-xs"
            >
              Void
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-700 mb-1">
        Sent to <span className="font-medium">{request.merchant_email}</span>
      </p>
      <CategoryPills request={request} />
      <div className="mt-1.5 flex flex-wrap gap-x-2 text-xs text-gray-400">
        {request.requested_by && (
          <span>
            Requested by {request.requested_by} &middot; {formatAbsoluteDateTime(request.created_at)}
          </span>
        )}
        {request.status === 'voided' && request.voided_at && (
          <span>
            &middot; Voided {request.voided_by ? `by ${request.voided_by} ` : ''}
            {formatAbsoluteDateTime(request.voided_at)}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Shows document requests for a lead, split into:
 *   - Active: pending requests the merchant can still act on
 *   - History: fulfilled, expired, or voided requests (collapsible)
 *
 * Visible to ops/management/super_admin only.
 */
export function PendingDocumentRequests({ anchor }: Props) {
  const { canRequestDocuments } = usePermissions()
  const { data, isLoading } = useDocumentRequests(anchor)
  const [historyOpen, setHistoryOpen] = useState(false)

  if (!canRequestDocuments()) return null

  const requests = data?.document_requests ?? []
  if (isLoading || requests.length === 0) return null

  const active = requests.filter((r) => r.status === 'pending')
  const history = requests.filter((r) => r.status !== 'pending')

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document Requests</CardTitle>
      </CardHeader>
      <CardContent>
        {active.length > 0 ? (
          <div className="space-y-3">
            {active.map((req) => (
              <RequestRow key={req.id} request={req} anchor={anchor} variant="active" />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No active requests.</p>
        )}

        {history.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-3">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="flex w-full items-center justify-between text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <span>Request history ({history.length})</span>
              <svg
                className={`h-4 w-4 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {historyOpen && (
              <div className="mt-3 space-y-2">
                {history.map((req) => (
                  <RequestRow key={req.id} request={req} anchor={anchor} variant="history" />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
